import React from 'react';
import styled, { css } from 'styled-components';
import { InputProps } from '@/types/components';
import { theme } from '@/config/theme';

const InputWrapper = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.gray[900]};
  font-family: 'Open Sans', sans-serif;
`;

const InputContainer = styled.div<{ $size: string }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  
  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return css`
          font-size: 12px;
        `;
      case 'large':
        return css`
          font-size: 16px;
        `;
      default:
        return css`
          font-size: 14px;
        `;
    }
  }}
`;

const StyledInput = styled.input<{
  $error: boolean;
  $size: string;
  $hasStartAdornment: boolean;
  $hasEndAdornment: boolean;
  $borderDark?: boolean;
}>`
  width: 100%;
  border: 1px solid ${({ $error, $borderDark, theme }) =>
    $error ? theme.colors.status.error : $borderDark ? theme.colors.gray[700] : theme.colors.border.primary};
  border-radius: 6px;
  background-color: ${theme.colors.white};
  color: ${theme.colors.gray[900]};
  transition: all 0.2s ease-in-out;
  line-height: 18px;
  font-family: 'Open Sans', sans-serif;
  
  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return css`
          padding: 4px 8px;
        `;
      case 'large':
        return css`
          padding: 16px 20px;
        `;
      default:
        return css`
          padding: 8px 12px;
        `;
    }
  }}

  ${({ $hasStartAdornment }) =>
    $hasStartAdornment &&
    css`
      padding-left: calc(32px + 8px);
    `}

  ${({ $hasEndAdornment }) =>
    $hasEndAdornment &&
    css`
      padding-right: calc(32px + 8px);
    `}

  &:focus {
    outline: none;
    border-color: ${({ $error, theme }) => 
      $error ? theme.colors.status.error : theme.colors.primary.accent};
    box-shadow: 0 0 0 3px ${({ $error, theme }) => 
      $error ? theme.colors.overlay.focusError : theme.colors.overlay.focus};
  }

  &:disabled {
    background-color: ${theme.colors.gray[50]};
    color: ${theme.colors.gray[400]};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${theme.colors.gray[550]};
  }

  /* Hide native date picker icon */
  &[type="date"]::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }

  &[type="date"]::-moz-calendar-picker-indicator {
    display: none;
  }

  /* Hide native time picker icon */
  &[type="time"]::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }

  &[type="time"]::-moz-calendar-picker-indicator {
    display: none;
  }
`;

const Adornment = styled.span<{ $position: 'start' | 'end' }>`
  position: absolute;
  ${({ $position }) => ($position === 'start' ? 'left' : 'right')}: 8px;
  display: flex;
  align-items: center;
  color: ${theme.colors.gray.muted};
  pointer-events: none;
  
  /* Allow pointer events for clickable elements inside */
  * {
    pointer-events: auto;
  }
`;

const HelperText = styled.span<{ $error: boolean }>`
  font-size: 12px;
  color: ${({ $error, theme }) => 
    $error ? theme.colors.status.error : theme.colors.gray.muted};
`;

const RequiredAsterisk = styled.span`
  margin-left: 2px;
`;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  value,
  onChange,
  placeholder,
  type = 'text',
  disabled = false,
  error = false,
  helperText,
  label,
  required = false,
  fullWidth = false,
  size = 'medium',
  startAdornment,
  endAdornment,
  className,
  name,
  id,
  borderVariant = 'default',
  ...rest
}, ref) => {
  return (
    <InputWrapper $fullWidth={fullWidth} className={className}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <RequiredAsterisk>*</RequiredAsterisk>}
        </Label>
      )}
      <InputContainer $size={size}>
        {startAdornment && (
          <Adornment $position="start">{startAdornment}</Adornment>
        )}
        <StyledInput
          ref={ref}
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          $error={error}
          $size={size}
          $hasStartAdornment={!!startAdornment}
          $hasEndAdornment={!!endAdornment}
          $borderDark={borderVariant === 'dark'}
          {...rest}
        />
        {endAdornment && (
          <Adornment $position="end">{endAdornment}</Adornment>
        )}
      </InputContainer>
      {helperText && (
        <HelperText $error={error}>{helperText}</HelperText>
      )}
    </InputWrapper>
  );
});

Input.displayName = 'Input';

export default Input;

